import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { ButtonSizes } from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'

import { useUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'

import type { PivotTableKey } from './pivotTable/utils'
import { ControlButton, SCROLLBAR_WIDTH } from './ui'

export type TabKey = 'FULL_MANIFEST' | PivotTableKey

// In display order
export const PIVOT_TAB_KEYS: TabKey[] = [
  'CATEGORY',
  'SUBCATEGORY',
  'BRAND',
  'MODEL',
  'SELLER_CATEGORY',
  'CONDITION',
  'SELLER_GRADE',
]

const Body = styled.div`
  background-color: ${Colors.Semantic.Neutral.light_grey};
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.75rem 1rem;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: ${SCROLLBAR_WIDTH}px;
  }
`

const Tab = styled(ControlButton)`
  border-color: ${Colors.Semantic.Neutral.medium_grey};
  flex-shrink: 0;
`

export const ManifestTabBar = ({
  activeTabKey,
  isLoggedIn,
  setActiveTabKey,
  showSellerGradeTab = false,
}: {
  activeTabKey: TabKey
  isLoggedIn: boolean
  setActiveTabKey: (key: TabKey) => void
  showSellerGradeTab?: boolean
}) => {
  const { formatMessage } = useIntl()
  const { openUnAuthModal } = useUnAuthWarningModal()

  const handleTabClick = (key: TabKey) => {
    if (PIVOT_TAB_KEYS.includes(key) && !isLoggedIn) {
      openUnAuthModal(
        formatMessage(
          { id: 'Listing.manifestAgGrid.viewPivotTable' },
          {
            pivotTableName: formatMessage({
              id: `Listing.manifestPivotTable.pivotTableKey.${key}`,
            }),
          }
        )
      )
      return
    }
    setActiveTabKey(key)
  }

  const visiblePivotTabKeys = PIVOT_TAB_KEYS.filter(
    (key) => key !== 'SELLER_GRADE' || showSellerGradeTab
  )

  return (
    <Body role="tablist">
      <Tab
        appearance={activeTabKey === 'FULL_MANIFEST' ? 'primary' : 'secondary'}
        aria-selected={activeTabKey === 'FULL_MANIFEST'}
        id="tab-FULL_MANIFEST"
        onClick={() => handleTabClick('FULL_MANIFEST')}
        role="tab"
        size={ButtonSizes.small}
      >
        {formatMessage({ id: 'Listing.manifestTabButtons.FULL_MANIFEST' })}
      </Tab>
      {visiblePivotTabKeys.map((key) => (
        <Tab
          $disabled={!isLoggedIn}
          appearance={activeTabKey === key ? 'primary' : 'secondary'}
          aria-disabled={!isLoggedIn || undefined}
          aria-selected={activeTabKey === key}
          id={`tab-${key}`}
          key={key}
          onClick={() => handleTabClick(key)}
          role="tab"
          size={ButtonSizes.small}
        >
          {formatMessage({
            id: `Listing.manifestPivotTable.pivotTableKey.${key}`,
          })}
        </Tab>
      ))}
    </Body>
  )
}
